import * as React from "react";

import { Skeleton, Text } from "@/components/ui";
import { cn } from "@/lib/utils/cn";

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "bg-card text-card-foreground border-border overflow-hidden rounded-lg border shadow-sm",
      className,
    )}
    {...props}
  />
));
Card.displayName = "Card";

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "border-border flex flex-col space-y-1.5 border-b p-5",
      className,
    )}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "text-foreground text-lg leading-none font-semibold tracking-tight",
      className,
    )}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-muted-foreground text-sm", className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-5", className)} {...props} />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center px-5 py-4", className)}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";

const CardLineItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    label: string;
    value?: string;
    isLoading: boolean;
  }
>(({ isLoading, label, value, ...props }, ref) => (
  <div
    className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0"
    ref={ref}
    {...props}
  >
    <Text variant="body" className="leading-6 font-semibold">
      {label}
    </Text>
    <Text
      variant="subHeading"
      className="mt-1 leading-6 whitespace-pre-wrap sm:col-span-2 sm:mt-0"
    >
      {isLoading ? <Skeleton className="h-4 w-[250px]" /> : value || "-"}
    </Text>
  </div>
));
CardLineItem.displayName = "CardLineItem";

export {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardLineItem,
  CardTitle,
};
